import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import AOS from "aos";
import "aos/dist/aos.css";
import { useNavigate } from "react-router-dom";

const HomeImageForm = () => {
  const [articles, setArticles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [loadingClients, setLoadingClients] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
    fetchArticles();
    fetchClients();
  }, []);

  // ✅ Fetch Articles
  const fetchArticles = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/articles/get-all-articles`
      );
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.articles || res.data.data || [];
      setArticles(data);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to load articles");
    } finally {
      setLoadingArticles(false);
    }
  };

  // ✅ Fetch Clients (company logos)
  const fetchClients = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/our-client/all-clients`
      );
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.clients || res.data.data || [];
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load client logos");
    } finally {
      setLoadingClients(false);
    }
  };

  // ✅ Navigate to article details with slug
  const handleArticleClick = (article) => {
    navigate(`/article/${article.slug || article._id}`);
  };

  // ✅ Navigate to all articles page
  const handleViewAllArticles = () => {
    navigate("/all-article");
  };

  return (
    <div className="px-4 md:px-10">
      {/* === OUR CLIENT SECTION === */}
      <section className="overflow-hidden">
        <div className="flex flex-col items-center gap-3 text-center py-6">
          <span
            data-aos="flip-up"
            className="border-b-2 font-semibold border-[#FF3131] py-2 tracking-wide text-[#FF3131]"
          >
            OUR CLIENT
          </span>
        </div>

        {loadingClients ? (
          <p className="text-center text-gray-500">Loading client logos...</p>
        ) : clients.length === 0 ? (
          <p className="text-center text-gray-500">No clients found.</p>
        ) : (
          <div className="overflow-hidden w-full">
            <div className="flex animate-scroll w-max gap-10">
              {[...clients, ...clients].map((client, i) => (
                <div
                  key={i}
                  className="flex flex-col justify-center items-center w-32 sm:w-40 gap-2 shrink-0"
                >
                  <img
                    src={Array.isArray(client.logo) ? client.logo[0] : client.logo}
                    alt={client.companyName || `Client ${i + 1}`}
                    className="w-full h-20 sm:h-24 object-contain"
                  />
                  <p className="text-xs text-center text-gray-600 font-medium leading-tight">
                    {client.companyName}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* === ARTICLES SECTION === */}
      <section>
        <div className="flex flex-col items-center gap-3 text-center py-6">
          <span
            data-aos="flip-up"
            className="border-b-2 font-semibold border-[#FF3131] py-2 tracking-wide text-[#FF3131]"
          >
            OUR ARTICLES
          </span>

          <h2
            data-aos="zoom-in-down"
            className="text-3xl md:text-4xl font-bold tracking-wide"
          >
            <span className="text-[#2F9BD6]">Latest </span>Articles Posts
          </h2>
        </div>

        {loadingArticles ? (
          <p className="text-center text-gray-500">Loading articles...</p>
        ) : articles.length === 0 ? (
          <p className="text-center text-gray-500">No articles found.</p>
        ) : (
          <>
            <div
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8"
              data-aos="fade-up"
            >
              {articles.slice(0, 4).map((article, i) => (
                <div
                  key={i}
                  onClick={() => handleArticleClick(article)}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-shadow duration-300 overflow-hidden flex flex-col cursor-pointer"
                  data-aos="zoom-in"
                >
                  {article.images && article.images.length > 0 && (
                    <img
                      src={article.images[0]}
                      alt={article.title}
                      className="w-full h-[200px] object-cover"
                    />
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <h2 className="text-xl font-bold text-[#191919] mb-2 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-gray-500 mb-3">
                      By {article.author}
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArticleClick(article);
                      }}
                      className="mt-4 self-start bg-[#2F9BD6] text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-[#2383b9] transition-all"
                    >
                      Read More
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* ✅ View All Button */}
            <div className="flex justify-center mt-10">
              <button
                onClick={handleViewAllArticles}
                className="bg-gradient-to-r from-[#FF3131] to-[#ff9301] text-white px-8 py-3 rounded-full font-semibold text-lg hover:opacity-90 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                View All Articles →
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default HomeImageForm;